/**
 * `@cet/asset-xapi` — xAPI 1.0.3 statements from what a host observed (D13: the host emits, this
 * project ships the builder).
 *
 * **Pure.** No I/O, no clock, no random: the statement id and the timestamp are passed in, so the
 * same events always build the same statements and a test can say exactly what they are. Sending
 * them is the host's business, with the host's LRS credential — never this package's, and never a
 * browser's.
 *
 * **An asset never builds one.** Everything here starts from host-side facts: the response the host
 * stored, the verdict the host computed or released, the score `/v1/score` returned. That is the
 * whole reason the format can change without touching a single asset.
 *
 * Vocabulary is ADL's verbs and the cmi5 progress extension, plus the one CET extension the old
 * library already used (`http://xapi.cet.ac.il/full_answer`). Nothing here invents an IRI an LRS
 * report would not recognise.
 */

import type { ItemResponse, ItemVerdict, LocalizedText, Score } from '@cet/asset-contract';

export const XAPI_VERSION = '1.0.3';

/**
 * Who the statements are about. A host's real learner; the test player's is a fake one.
 *
 * A launcher's actor is passed on as it wrote it: Focus sends an `mbox` beside the `account`, which
 * strict xAPI would refuse, and its own LRS reads it — so this carries both rather than choosing.
 */
export interface Agent {
  readonly objectType: 'Agent';
  readonly name?: string;
  readonly mbox?: string;
  readonly account: { readonly homePage: string; readonly name: string };
}

export interface XapiContext {
  readonly actor: Agent;
  /** Where activity IRIs are rooted, e.g. `https://assets-platform.cet.ac.il/activities`. */
  readonly activityBase: string;
  readonly asset: { readonly id: string; readonly version: string; readonly title: LocalizedText };
  /** One attempt. A new one at every reset or new attempt. A UUID. */
  readonly registration: string;
  readonly locale: string;
  /** What played it, e.g. `CET asset player`. */
  readonly platform: string;
}

/** What the host saw. Each becomes one statement, except where a set of marks arrives at once. */
export type XapiEvent =
  | { readonly kind: 'launched' }
  | { readonly kind: 'page'; readonly index: number; readonly count: number; readonly stepId?: string }
  /**
   * A page is done — the asset said so, and every question on it was checked. `verdicts` are the
   * marks of its questions, released by the host; none for a page of content alone.
   */
  | {
      readonly kind: 'pageCompleted';
      readonly index: number;
      readonly count: number;
      readonly stepId?: string;
      readonly verdicts: readonly ItemVerdict[];
      /** The outcome of a talk held on this page (D99), as on `completed` — 720 namespace only. */
      readonly extensions?: ResultExtensions;
    }
  | {
      readonly kind: 'answered';
      readonly itemId: string;
      readonly response: ItemResponse;
      /** The verdict this answer got as it arrived — only under `onAnswer`; otherwise null. */
      readonly verdict: ItemVerdict | null;
      /** The page the question is on, when the host knows it: the statement's parent (see `OnPage`). */
      readonly page?: OnPage;
    }
  | { readonly kind: 'progressed'; readonly fraction: number }
  /** The learner was given a hint (D48). */
  | { readonly kind: 'hinted'; readonly itemId: string; readonly hintId: string; readonly index: number; readonly page?: OnPage }
  /** The learner was shown a question's answer (Show solution, D66) — Focus's "reveal answers". */
  | { readonly kind: 'revealed'; readonly itemId: string; readonly page?: OnPage }
  /** Marks released at a Check: one `scored` statement per item. */
  | { readonly kind: 'checked'; readonly verdicts: readonly ItemVerdict[] }
  /**
   * The attempt is complete. `extensions` (D96): result extensions under CET's 720 namespace,
   * such as the speaking bot's outcome (`freeTalkOutcomeExtensions`); anything else is dropped.
   */
  | { readonly kind: 'completed'; readonly extensions?: ResultExtensions }
  /**
   * The submit that ended the attempt: `completed` unless this attempt already sent one, then
   * `passed` or `failed` when the scheme sets a pass mark, then one `scored` per item.
   */
  | {
      readonly kind: 'submitted';
      readonly total: Score;
      readonly perItem: readonly ItemVerdict[];
      /** True when this attempt's `completed` was already sent — the asset said it finished. */
      readonly alreadyCompleted: boolean;
      /**
       * `points`: the attempt's score is the scheme's points share — each question weighs its points —
       * on 0–100 like every score (64 of 85 points is raw 75.29, scaled 0.7529). A placement test
       * (Focus 084 reads its `scaled`). Absent: the profile's own reading.
       */
      readonly results?: 'points';
      /** Each part's score, when the scheme names parts (D93) — carried on the result, 0–100 each. */
      readonly parts?: Readonly<Record<string, Score>>;
      /** As on `completed` (D96): rides the attempt's `completed`, or its result statement when `completed` already went. */
      readonly extensions?: ResultExtensions;
    };

/** Result extensions a host attaches to the attempt's completion — keys are IRIs. */
export type ResultExtensions = Readonly<Record<string, unknown>>;

/**
 * The page a question is on. A question's statement then names the page as its parent, and the
 * activity as its grouping — what the Ministry of Education's profile requires of an answer, so a
 * platform can attach the answer to its page. Without one, the parent is
 * the activity.
 */
export interface OnPage {
  readonly index: number;
  readonly count: number;
  /**
   * The lesson step this page plays, when the asset is a lesson built from the widget kit. The page
   * is then named by the step's IRI (`stepIri`), the same string the studio's Focus export writes as
   * the item's `itemKey` (D77), so Focus matches the two exactly. A question keeps its own id.
   */
  readonly stepId?: string;
}

export interface Statement {
  readonly id: string;
  readonly version: string;
  readonly timestamp: string;
  readonly actor: Agent;
  readonly verb: { readonly id: string; readonly display: Readonly<Record<string, string>> };
  readonly object: {
    readonly objectType: 'Activity';
    readonly id: string;
    readonly definition: {
      readonly type: string;
      readonly name?: Readonly<Record<string, string>>;
      readonly interactionType?: string;
      readonly extensions?: Readonly<Record<string, unknown>>;
    };
  };
  readonly result?: Readonly<Record<string, unknown>>;
  readonly context: Readonly<Record<string, unknown>>;
}

/** Ids and the time, supplied by the caller — this package has neither a clock nor a random source. */
export interface Stamp {
  readonly timestamp: string;
  /** Called once per statement built. */
  nextId(): string;
}

const ADL = 'http://adlnet.gov/expapi';
const adlVerb = (name: string): string => `${ADL}/verbs/${name}`;

/**
 * The moments a host reports, each of which a profile names a verb for.
 *
 * A moment is what happened (the asset loaded, a page was entered, an item was scored); the verb is
 * what one product's reports call it. Keeping the two apart is what lets the 720 flow say
 * `initialized` where the default says `launched` without a second builder.
 */
export const MOMENTS = [
  'launched',
  'pageEntered',
  'pageCompleted',
  'answered',
  'progressed',
  'hintRequested',
  'solutionRequested',
  'itemScored',
  'completed',
  'passed',
  'failed',
  'activityScored',
] as const;
export type Moment = (typeof MOMENTS)[number];

/**
 * One product's xAPI vocabulary: the verb IRI for each moment.
 *
 * Data, not code, so a provider chooses a profile by name and a real host's backend can use the
 * same profile as the player. The display name is the IRI's last segment.
 */
export interface XapiProfile {
  readonly id: string;
  readonly verbs: Readonly<Record<Moment, string>>;
  /** Moments this profile never reports — focus sends no progress and no per-item score. */
  readonly silent: readonly Moment[];
  /**
   * How a submit reads. `completedThenJudged`: cmi5's `completed`, then `passed`/`failed` — two
   * facts, two statements. `completedWithResult`: one `completed` carrying the score and success,
   * which is what Focus's reports read.
   */
  readonly submit: 'completedThenJudged' | 'completedWithResult';
  /** Added to every question's activity definition, e.g. the Ministry of Education question marker. */
  readonly questionExtensions?: Readonly<Record<string, unknown>>;
  /**
   * `points`: a result is the points scored of the points available — the scheme's own arithmetic.
   * `meanPercent`: as Focus reads a result — the mean of the questions' scores, each question
   * weighing the same (a right and a wrong answer score 50), on a 0–100 scale. For a page and for
   * the attempt alike, so the two read the same way.
   */
  readonly results: 'points' | 'meanPercent';
  /**
   * The scaled score a result counts as a success at, when nothing declares a pass mark — a page
   * never does, and an activity may not. Focus reads `success`, and counts `scaled >= 0.7` as one. The scheme's own pass mark always wins. Absent: no `success` without one.
   */
  readonly successAt?: number;
}

/** The vocabulary the player has used from the start: ADL verbs, one per moment. */
export const DEFAULT_PROFILE: XapiProfile = {
  id: 'default',
  verbs: {
    launched: adlVerb('launched'),
    pageEntered: adlVerb('experienced'),
    pageCompleted: adlVerb('completed'),
    answered: adlVerb('answered'),
    progressed: adlVerb('progressed'),
    hintRequested: adlVerb('requested'),
    solutionRequested: adlVerb('requested'),
    itemScored: adlVerb('scored'),
    completed: adlVerb('completed'),
    passed: adlVerb('passed'),
    failed: adlVerb('failed'),
    activityScored: adlVerb('scored'),
  },
  // The player has always reported where the learner is, not which pages they finished, and not
  // which answers they were shown.
  silent: ['pageCompleted', 'solutionRequested'],
  submit: 'completedThenJudged',
  results: 'points',
};

/**
 * Focus (720): the vocabulary Focus's LRS reads (docs/proposals/player-providers.md, A.2).
 *
 * `initialized` where the default says `launched` — for the activity and for every page entry;
 * `answered` at Check, with the score; `requested` for a hint; `completed` for a page, and one
 * `completed` for the attempt at its finish — both scored as Focus reads them, the mean of the
 * questions on 0–100, a success at 0.7 unless the scheme declares a pass mark. No progress and no
 * per-item `scored`, which focus never sends. The marks are still ours (`/v1/score`), never a
 * client's.
 */
export const FOCUS_PROFILE: XapiProfile = {
  id: 'focus',
  verbs: {
    launched: adlVerb('initialized'),
    pageEntered: adlVerb('initialized'),
    pageCompleted: adlVerb('completed'),
    answered: adlVerb('answered'),
    progressed: adlVerb('progressed'),
    hintRequested: adlVerb('requested'),
    solutionRequested: adlVerb('requested'),
    itemScored: adlVerb('scored'),
    completed: adlVerb('completed'),
    passed: adlVerb('passed'),
    failed: adlVerb('failed'),
    activityScored: adlVerb('scored'),
  },
  silent: ['progressed', 'itemScored'],
  submit: 'completedWithResult',
  questionExtensions: { 'https://lxp.education.gov.il/xapi/moe/activities/question': true },
  results: 'meanPercent',
  successAt: 0.7,
};

/** Every profile, by name. A provider names one; an unknown name is refused, never guessed. */
export const PROFILES: Readonly<Record<string, XapiProfile>> = {
  default: DEFAULT_PROFILE,
  focus: FOCUS_PROFILE,
};

export const profileNamed = (id: string): XapiProfile | null => PROFILES[id] ?? null;

const verbOf = (profile: XapiProfile, moment: Moment): Statement['verb'] => {
  const id = profile.verbs[moment];
  return { id, display: { 'en-US': id.slice(id.lastIndexOf('/') + 1) } };
};
const ACTIVITY_TYPE = {
  asset: `${ADL}/activities/assessment`,
  interaction: `${ADL}/activities/cmi.interaction`,
  page: `${ADL}/activities/module`,
} as const;
const PROGRESS = 'https://w3id.org/xapi/cmi5/result/extensions/progress';
const FULL_ANSWER = 'http://xapi.cet.ac.il/full_answer';
/** Each part's score beside the total, `{ partId: score }` — a placement test's levels (D93). */
const PARTS = 'http://xapi.cet.ac.il/parts';
/** The words a question's answer was marked with — the AI's, for an open answer (D93). */
const FEEDBACK = 'http://xapi.cet.ac.il/feedback';
/** An open answer the AI scored by rubric: its points by criterion, `{ criterionId: points }` (D110). */
const RUBRIC_SCORES = 'http://xapi.cet.ac.il/rubric_scores';
/** The AI's notes on an answer for a teacher, e.g. `["creative_intent"]` (D110). */
const AI_FLAGS = 'http://xapi.cet.ac.il/ai_flags';

/**
 * Every open answer the AI scored by rubric or flagged, on the attempt's result — `{ itemId: { scores?,
 * flags? } }` (D110) — since a profile may keep the questions' own `scored` statements silent, as Focus's does.
 */
const AI_JUDGED = 'http://xapi.cet.ac.il/ai_judged';

/** A verdict's rubric scores and flags (D110) as result extensions; nothing for any other verdict. */
const judgedExtensions = (verdict: ItemVerdict | null | undefined): Record<string, unknown> => ({
  ...(verdict?.scores === undefined ? {} : { [RUBRIC_SCORES]: verdict.scores }),
  ...(verdict?.flags === undefined || verdict.flags.length === 0 ? {} : { [AI_FLAGS]: verdict.flags }),
});
/** The Ministry of Education's kind of help asked for — what marks a revealed answer for Focus. */
const HELP_TYPE = 'https://lxp.education.gov.il/xapi/moe/extensions/helpType';
/**
 * CET's 720 extension namespace — the one Focus reads a speaking practice's result under (Focus
 * spec 080, contracts/practice-statement.md). The only namespace a host may put on `completed`.
 */
export const CET_720_EXTENSION_NS = 'https://content.cet.ac.il/720/xapi/ext/';
/** The whole outcome body is dropped past this size; the six summary keys still go. */
export const FREE_TALK_OUTCOME_MAX_BYTES = 16_384;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const count = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : undefined;
const shortText = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 && value.length <= 200 ? value : undefined;
/** A string's length in UTF-8 — counted here, as the contract does, so this package needs no runtime's encoder. */
function utf8Bytes(text: string): number {
  let bytes = 0;
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
  }
  return bytes;
}

/**
 * The speaking bot's outcome (Free-Talk 033 `conversation_outcome`, handed over in the kit's
 * `finished` since its 034) as Focus's result extensions (D96): the six keys Focus already reads —
 * the result, the end state, the goals as counts, the Can-Do references, the learner turns and the
 * active seconds — and the whole body under `conversation-outcome`, bounded. Nothing is invented:
 * a part the body lacks is left out, and a body that is not an outcome yields nothing.
 */
export function freeTalkOutcomeExtensions(outcome: unknown): ResultExtensions {
  if (!isRecord(outcome) || outcome['kind'] !== 'conversation_outcome') return {};
  const ns = CET_720_EXTENSION_NS;
  const built: Record<string, unknown> = {};
  const result = isRecord(outcome['result']) ? outcome['result'] : null;
  const status = result === null ? undefined : result['outcome'];
  if (status === 'met' || status === 'almost' || status === 'not_yet') built[`${ns}result`] = status;
  const endState = shortText(outcome['endState']);
  if (endState !== undefined) built[`${ns}end-state`] = endState;
  const task = result !== null && isRecord(result['task']) ? result['task'] : null;
  if (task !== null) {
    const goals = Array.isArray(task['goals']) ? task['goals'].filter(isRecord) : [];
    const total = count(task['goalsTotal']) ?? goals.length;
    built[`${ns}goals`] = {
      total,
      reached: goals.filter((goal) => goal['reached'] === true).length,
      reachedWithoutHelp: goals.filter((goal) => goal['reached'] === true && goal['withHelp'] !== true).length,
    };
  }
  if (Array.isArray(outcome['canDo'])) {
    const refs = outcome['canDo']
      .filter(isRecord)
      .map((entry) => shortText(entry['ref']) ?? shortText(entry['text']))
      .filter((ref): ref is string => ref !== undefined);
    if (refs.length > 0) built[`${ns}can-do`] = refs;
  }
  const reported = isRecord(outcome['reported']) ? outcome['reported'] : null;
  const turns = reported === null ? undefined : count(reported['learnerTurns']);
  if (turns !== undefined) built[`${ns}learner-turns`] = turns;
  const seconds = count(outcome['durationSeconds']);
  if (seconds !== undefined) built[`${ns}active-seconds`] = seconds;
  if (utf8Bytes(JSON.stringify(outcome)) <= FREE_TALK_OUTCOME_MAX_BYTES) {
    built[`${ns}conversation-outcome`] = outcome;
  }
  return built;
}

/** Only the 720 namespace rides a host's `completed`; a key from anywhere else is dropped. */
function resultExtensions(extensions: ResultExtensions | undefined): { extensions?: Record<string, unknown> } {
  if (extensions === undefined) return {};
  const kept = Object.fromEntries(Object.entries(extensions).filter(([key]) => key.startsWith(CET_720_EXTENSION_NS)));
  return Object.keys(kept).length === 0 ? {} : { extensions: kept };
}

/**
 * The activity's IRI: `<activityBase>/<activity id>`. The one definition of it — the player's
 * statements and the studio's Focus export both call this, so the ids Focus imports and the ids it
 * is sent cannot drift apart (D77).
 */
export const activityIri = (activityBase: string, activityId: string): string =>
  `${activityBase.replace(/\/$/, '')}/${encodeURIComponent(activityId)}`;

/**
 * A lesson step's IRI: `<activity IRI>/items/<stepId>` — Focus's item. Focus matches a statement to
 * an item when its object id equals the item's `itemKey`, so the export writes exactly this string.
 */
export const stepIri = (activityBase: string, activityId: string, stepId: string): string =>
  `${activityIri(activityBase, activityId)}/items/${encodeURIComponent(stepId)}`;

/**
 * A question's IRI: `<activity IRI>/questions/<content item id>`. Focus matches a question by the
 * last segment — the export's `questionId` — so the path before it is for whoever reads the
 * statements: a step is an `items/` id, a question a `questions/` one.
 */
export const questionIri = (activityBase: string, activityId: string, questionId: string): string =>
  `${activityIri(activityBase, activityId)}/questions/${encodeURIComponent(questionId)}`;

const assetIri = (context: XapiContext): string => activityIri(context.activityBase, context.asset.id);

/** A language map from the contract's localized text, dropping empty entries. */
function languageMap(text: LocalizedText): Record<string, string> {
  const map: Record<string, string> = {};
  for (const [language, value] of Object.entries(text as Record<string, string | undefined>)) {
    if (typeof value === 'string' && value !== '') map[language] = value;
  }
  return map;
}

/**
 * The xAPI interaction type and the spec's response string for one contract response.
 *
 * The string formats are the spec's (`[,]` between choices, `[.]` inside a matched pair), so an LRS
 * report can read them. The whole response also travels in `full_answer`, because a string is lossy.
 */
export function describeResponse(response: ItemResponse): { interactionType: string; response: string } {
  if ('choiceId' in response) return { interactionType: 'choice', response: response.choiceId };
  if ('choiceIds' in response) return { interactionType: 'choice', response: response.choiceIds.join('[,]') };
  if ('text' in response) return { interactionType: 'fill-in', response: response.text };
  if ('order' in response) return { interactionType: 'sequencing', response: response.order.join('[,]') };
  if ('pairs' in response) {
    return {
      interactionType: 'matching',
      response: Object.entries(response.pairs)
        .map(([left, right]) => `${left}[.]${right}`)
        .join('[,]'),
    };
  }
  return {
    interactionType: response.values.length === 1 ? 'numeric' : 'fill-in',
    response: response.values.join('[,]'),
  };
}

/**
 * Every score on one scale, in every profile (D91): `raw` 0–100 and `scaled` 0–1 — the points share,
 * each question weighing its scheme `points`. Nothing worth points scores 0.
 */
const scoreOf = (raw: number, max: number): { raw: number; min: number; max: number; scaled: number } => {
  const share = max > 0 ? raw / max : 0;
  return { raw: Math.round(share * 10_000) / 100, min: 0, max: 100, scaled: Math.round(share * 10_000) / 10_000 };
};

/**
 * Focus's score for a set of questions: the mean of their scores, each question weighing the same, on
 * 0–100 — four places scaled, two on the percent, the same number both ways round. A question worth
 * nothing is not part of the mean; with none left there is no score.
 */
function meanPercent(verdicts: readonly ItemVerdict[]): { raw: number; min: number; max: number; scaled: number } | null {
  const marked = verdicts.filter((verdict) => verdict.max > 0);
  if (marked.length === 0) return null;
  const mean = marked.reduce((sum, verdict) => sum + verdict.points / verdict.max, 0) / marked.length;
  return { raw: Math.round(mean * 10_000) / 100, min: 0, max: 100, scaled: Math.round(mean * 10_000) / 10_000 };
}

/** Every statement built for one event — usually one. */
export function statementsFor(
  event: XapiEvent,
  context: XapiContext,
  stamp: Stamp,
  profile: XapiProfile = DEFAULT_PROFILE,
): Statement[] {
  const asset = assetIri(context);
  const assetActivity: Statement['object'] = {
    objectType: 'Activity',
    id: asset,
    definition: { type: ACTIVITY_TYPE.asset, name: languageMap(context.asset.title) },
  };

  const built: Statement[] = [];
  /** Builds one statement for a moment — or none, when this profile keeps that moment silent. */
  /** Where a statement sits, beyond the activity: the page a question is on, and context extensions. */
  interface Placing {
    readonly page?: OnPage | undefined;
    readonly extensions?: Readonly<Record<string, unknown>>;
  }
  const statement = (moment: Moment, object: Statement['object'], result?: Record<string, unknown>, placing: Placing = {}): void => {
    if (profile.silent.includes(moment)) return;
    built.push(make(moment, object, result, placing));
  };
  const parents = (object: Statement['object'], on: OnPage | undefined): Record<string, unknown> => {
    if (object.id === asset) return {};
    if (on === undefined) return { contextActivities: { parent: [assetActivity] } };
    return { contextActivities: { parent: [page(on.index, on.count, on.stepId)], grouping: [assetActivity] } };
  };
  const make = (moment: Moment, object: Statement['object'], result: Record<string, unknown> | undefined, placing: Placing): Statement => ({
    id: stamp.nextId(),
    version: XAPI_VERSION,
    timestamp: stamp.timestamp,
    actor: context.actor,
    verb: verbOf(profile, moment),
    object,
    ...(result === undefined ? {} : { result }),
    context: {
      registration: context.registration,
      platform: context.platform,
      language: context.locale,
      // The asset's version is the activity's revision: the same activity, a different build of it.
      revision: context.asset.version,
      ...parents(object, placing.page),
      ...(placing.extensions === undefined ? {} : { extensions: placing.extensions }),
    },
  });

  /**
   * A question, by its own id: `<activity>/questions/<content item id>` (`questionIri`), whose last
   * segment is the export's `questionId` — what Focus matches a question by (D77). Its page is its
   * parent.
   */
  const item = (itemId: string, interactionType?: string): Statement['object'] => ({
    objectType: 'Activity',
    id: questionIri(context.activityBase, context.asset.id, itemId),
    definition: {
      type: ACTIVITY_TYPE.interaction,
      name: { en: itemId },
      ...(interactionType === undefined ? {} : { interactionType }),
      ...(profile.questionExtensions === undefined ? {} : { extensions: profile.questionExtensions }),
    },
  });

  /** A page: its lesson step's IRI when it plays one, else its place in the activity. */
  const page = (index: number, count: number, stepId?: string): Statement['object'] => ({
    objectType: 'Activity',
    id:
      stepId === undefined
        ? `${asset}/pages/${index + 1}`
        : stepIri(context.activityBase, context.asset.id, stepId),
    definition: { type: ACTIVITY_TYPE.page, name: { en: `Page ${index + 1} of ${count}` } },
  });

  /** `success` by the profile's threshold, when it has one and nothing declared a pass mark. */
  const successOf = (scaled: number): { success?: boolean } =>
    profile.successAt === undefined ? {} : { success: scaled >= profile.successAt };

  const scored = (verdict: ItemVerdict): void => {
    const extensions = judgedExtensions(verdict);
    statement('itemScored', item(verdict.itemId), {
      score: scoreOf(verdict.points, verdict.max),
      // `correct` is null for an item nobody can mark right or wrong, and `success` must be a boolean.
      ...(verdict.answered && typeof verdict.correct === 'boolean' ? { success: verdict.correct } : {}),
      ...(Object.keys(extensions).length === 0 ? {} : { extensions }),
    });
  };

  switch (event.kind) {
    case 'launched':
      statement('launched', assetActivity);
      break;

    case 'page':
      statement('pageEntered', page(event.index, event.count, event.stepId));
      break;

    /**
     * A page is scored as Focus reads one — the mean of its questions, on 0–100 — whatever the
     * profile's `results`, since a page has no points total of its own. A page of content alone is
     * completed with no score.
     */
    case 'pageCompleted': {
      const score = meanPercent(event.verdicts);
      statement('pageCompleted', page(event.index, event.count, event.stepId), {
        completion: true,
        ...(score === null ? {} : { score, ...successOf(score.scaled) }),
        // D99: a talk held on this page reports its outcome with the page, not only at the end.
        ...resultExtensions(event.extensions),
      });
      break;
    }

    case 'answered': {
      const { interactionType, response } = describeResponse(event.response);
      const verdict = event.verdict;
      // Focus's answer: its score on 0–100 and a success at the profile's threshold. The points, and
      // the verdict's own right-or-wrong, everywhere else.
      const percent = profile.results === 'meanPercent' && verdict !== null ? meanPercent([verdict]) : null;
      statement(
        'answered',
        item(event.itemId, interactionType),
        {
          response,
          // Only a verdict for THIS answer — as it arrived, or at the Check that marked it. Without one
          // there is no judgement yet, and saying `success: false` would record one nobody made.
          ...(percent !== null
            ? { success: profile.successAt === undefined ? verdict?.correct === true : percent.scaled >= profile.successAt }
            : verdict !== null && verdict.answered && typeof verdict.correct === 'boolean'
              ? { success: verdict.correct }
              : {}),
          ...(verdict === null ? {} : { score: percent ?? scoreOf(verdict.points, verdict.max) }),
          extensions: {
            [FULL_ANSWER]: event.response,
            ...(typeof verdict?.message === 'string' && verdict.message !== '' ? { [FEEDBACK]: verdict.message } : {}),
            ...judgedExtensions(verdict),
          },
        },
        { page: event.page },
      );
      break;
    }

    case 'progressed':
      statement('progressed', assetActivity, {
        extensions: { [PROGRESS]: Math.round(Math.min(Math.max(event.fraction, 0), 1) * 100) },
      });
      break;

    case 'hinted':
      statement('hintRequested', item(event.itemId), undefined, { page: event.page });
      break;

    // A help request like a hint, told apart by the MoE help type.
    case 'revealed':
      statement('solutionRequested', item(event.itemId), undefined, {
        page: event.page,
        extensions: { [HELP_TYPE]: 'reveal-answers' },
      });
      break;

    case 'checked':
      event.verdicts.forEach(scored);
      break;

    case 'completed':
      statement('completed', assetActivity, { completion: true, ...resultExtensions(event.extensions) });
      break;

    /**
     * Two facts, two statements, as cmi5 and the reports built on it expect: `completed` says the
     * attempt is finished, `passed`/`failed` says how it was judged. One statement carrying both
     * reads as "not completed" in most LRS reports. `completed` goes once per attempt, so not again
     * when the asset already said it finished; with no pass mark there is nothing to pass or fail,
     * and the score rides on `completed` (or on `scored`, if `completed` already went).
     */
    case 'submitted': {
      const { total } = event;
      // 0–100 either way: Focus's mean of the questions, or the points share (a placement test, and
      // every other profile).
      const byMean = profile.results === 'meanPercent' && event.results !== 'points';
      const score = (byMean ? meanPercent(event.perItem) : null) ?? scoreOf(total.raw, total.max);
      const judged = total.passed !== undefined;
      const success = judged ? { success: total.passed } : successOf(score['scaled'] ?? 0);
      // D96: the host's result extensions ride the attempt's `completed` — or, when `completed`
      // already went, the one statement that carries the result instead. Each part's score (D93)
      // rides the statement that carries the result, beside them.
      const extra = resultExtensions(event.extensions).extensions ?? {};
      // D110: the AI's rubric scores and flags travel with the parts, on the statement that carries the result.
      const aiJudged = Object.fromEntries(event.perItem
        .filter((verdict) => verdict.scores !== undefined || (verdict.flags?.length ?? 0) > 0)
        .map((verdict) => [verdict.itemId, {
          ...(verdict.scores === undefined ? {} : { scores: verdict.scores }),
          ...((verdict.flags?.length ?? 0) > 0 ? { flags: verdict.flags } : {}),
        }]));
      const parts: Record<string, unknown> = {
        ...(event.parts === undefined ? {}
          : { [PARTS]: Object.fromEntries(Object.entries(event.parts).map(([partId, part]) => [partId, scoreOf(part.raw, part.max)])) }),
        ...(Object.keys(aiJudged).length === 0 ? {} : { [AI_JUDGED]: aiJudged }),
      };
      const withExtensions = (...sets: readonly Record<string, unknown>[]): { extensions?: Record<string, unknown> } => {
        const merged = sets.reduce<Record<string, unknown>>((acc, set) => ({ ...acc, ...set }), {});
        return Object.keys(merged).length === 0 ? {} : { extensions: merged };
      };
      if (profile.submit === 'completedWithResult') {
        // One statement carrying the whole result — or, if `completed` already went, the result
        // alone, since an attempt completes once.
        if (event.alreadyCompleted) statement('activityScored', assetActivity, { score, ...success, ...withExtensions(extra, parts) });
        else statement('completed', assetActivity, { completion: true, score, ...success, ...withExtensions(extra, parts) });
      } else {
        if (!event.alreadyCompleted) {
          statement('completed', assetActivity, { completion: true, ...(judged ? {} : { score }), ...withExtensions(extra, judged ? {} : parts) });
        }
        const late = event.alreadyCompleted ? extra : {};
        if (judged) statement(total.passed ? 'passed' : 'failed', assetActivity, { score, success: total.passed, ...withExtensions(late, parts) });
        else if (event.alreadyCompleted) statement('activityScored', assetActivity, { score, ...withExtensions(late, parts) });
      }
      event.perItem.forEach(scored);
      break;
    }
  }
  return built;
}

/*
 * ----------------------------------------------------------------------------------------------
 * The speaking bot's events, relayed to Focus's LRS as statements (720 design 40 C14; D100).
 *
 * Free-Talk holds no person for a lesson (its 027) and cannot reach Focus's LRS: it posts each event
 * to the player's server (`POST /api/v1/freetalk/events`, C8), which turns it into the statement
 * below and posts that with the launch's own token, as it does the player's. The learner is the
 * launch's actor, the activity the lesson's asset, the registration the attempt's — the same three
 * the player's statements carry, so Focus reads both with one reader. The event body travels whole
 * under one extension, never a field of it reinterpreted here.
 *
 * Pure, as the rest of this package: the statement id is the event's id, so a retry of the same
 * event is the same statement and an LRS keeps one.
 */

/** The verbs Focus's ingest reads (its `xapi_classifier`). */
export const FREE_TALK_VERB = {
  result: 'http://xapi.cet.ac.il/verbs/freetalk-result',
  distress: 'http://xapi.cet.ac.il/verbs/freetalk-distress',
} as const;
/** The extensions the bodies travel under. */
export const FREE_TALK_EXTENSION = {
  result: 'http://xapi.cet.ac.il/freetalk_result',
  distress: 'http://xapi.cet.ac.il/freetalk_distress',
  /** The hub refused a learner's words with `distress` on an AI question: the flag, never the words. */
  aiFlag: 'http://xapi.cet.ac.il/ai_flag',
} as const;

/** The launch the statement is about: the three facts the player keeps for a Focus launch. */
export interface FreeTalkLaunch {
  readonly actor: Agent;
  readonly activityBase: string;
  readonly assetId: string;
  readonly registration: string;
}

const freeTalkActivity = (launch: FreeTalkLaunch): Statement['object'] => ({
  objectType: 'Activity',
  id: activityIri(launch.activityBase, launch.assetId),
  definition: { type: ACTIVITY_TYPE.asset },
});

const freeTalkStatement = (
  launch: FreeTalkLaunch,
  id: string,
  timestamp: string,
  verb: Statement['verb'],
  object: Statement['object'],
  extensions: Readonly<Record<string, unknown>>,
): Statement => ({
  id,
  version: XAPI_VERSION,
  timestamp,
  actor: launch.actor,
  verb,
  object,
  result: { extensions },
  context: { registration: launch.registration, platform: 'Free-Talk via CET asset player' },
});

/**
 * A `conversation_outcome` event (Free-Talk 033) as a `freetalk-result` statement: the whole C8 body
 * under `freetalk_result`, stamped with the conversation's end.
 */
export function freeTalkResultStatement(launch: FreeTalkLaunch, event: { readonly eventId: string; readonly endedAt: string } & Record<string, unknown>): Statement {
  return freeTalkStatement(
    launch,
    event.eventId,
    event.endedAt,
    { id: FREE_TALK_VERB.result, display: { en: 'completed a conversation' } },
    freeTalkActivity(launch),
    { [FREE_TALK_EXTENSION.result]: event },
  );
}

/**
 * A `distress_alert` event (Free-Talk 027/036) as a `freetalk-distress` statement: the alert's
 * identity and time under `freetalk_distress`. Nothing the learner said is in it — Free-Talk sends
 * none, and this adds none.
 */
export function freeTalkDistressStatement(launch: FreeTalkLaunch, event: { readonly eventId: string; readonly firedAt: string; readonly sessionId: string } & Record<string, unknown>): Statement {
  return freeTalkStatement(
    launch,
    event.eventId,
    event.firedAt,
    { id: FREE_TALK_VERB.distress, display: { en: 'showed signs of distress' } },
    freeTalkActivity(launch),
    { [FREE_TALK_EXTENSION.distress]: event },
  );
}

/**
 * The AI hub's `distress` refusal of an AI question's answer (D86) as an `answered` statement on
 * the question, flagged under `ai_flag` — the same verb Focus's ingest reads the question's other
 * answers with, carrying the flag only: the service that saw it and when. No answer text.
 */
export function aiFlagStatement(
  launch: FreeTalkLaunch,
  flag: { readonly eventId: string; readonly firedAt: string; readonly questionId: string; readonly service: string },
): Statement {
  return freeTalkStatement(
    launch,
    flag.eventId,
    flag.firedAt,
    { id: adlVerb('answered'), display: { en: 'answered' } },
    {
      objectType: 'Activity',
      id: questionIri(launch.activityBase, launch.assetId, flag.questionId),
      definition: { type: ACTIVITY_TYPE.interaction },
    },
    { [FREE_TALK_EXTENSION.aiFlag]: { kind: 'distress', eventId: flag.eventId, firedAt: flag.firedAt, service: flag.service, source: 'player_ai' } },
  );
}
